/**
 * ============================================================================
 * BUDGET.JS v19.0 - OPTIMIERTE DEPENDENCIES
 * ============================================================================
 * 
 * ✅ v19.0: DEPENDENCY OPTIMIERUNG
 *           - view aus Token-useEffect Dependencies ENTFERNT
 *           - Verhindert unnötige Re-Runs bei Tab-Wechsel
 *           - initialView wird jetzt als Konstante 'overview' verwendet
 * ✅ v18.4: ANALYTICS TRACKING (wie Assistent.js v14.1)
 *           - trackAnalyticsEvent() via /track/batch
 *           - component_view beim Mount
 *           - budget_transaction_create/edit/delete
 *           - budget_category_create/edit/delete
 *           - budget_limit_create/delete
 *           - budget_contribution (Sparziel)
 *           - budget_tab_change (View-Wechsel)
 * ✅ v18.3: FIX - Kategorien Deduplizierung
 * ✅ v18.2: Added onAdd prop to BudgetTransactions
 * ✅ v18.1: Erweitertes CSS für Planivio Branding
 * ✅ v18.0: PLANIVIO Rebranding
 * 
 * @version 19.0.0 - Dependency Optimierung
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
// ✅ v25.2: useAuth statt useLogto (Native-kompatibel!)
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from '../../context/I18nContext';
import { useTheme } from '../../context/ThemeContext';
import Loading, { Skeleton, SkeletonGrid } from '../common/Loading';
import BudgetCategories from './BudgetCategories';
import BudgetTransactions from './BudgetTransactions';
import BudgetLimits from './BudgetLimits';
import CreateCategoryModal from './CreateCategoryModal';
import CreateTransactionModal from './CreateTransactionModal';
import CreateLimitModal from './CreateLimitModal';
import RecurringTransactions from './RecurringTransactions';
import SavingsGoals from './SavingsGoals';
import Debts from './Debts';
import budgetService from '../../services/budget.service';
import './Budget.css';

console.log('💰 Budget.js v19.0 loaded - OPTIMIERTE DEPENDENCIES');

// ============================================================================
// ✅ v18.4: ANALYTICS TRACKING (wie Assistent.js v14.1)
// ============================================================================
const API_URL = process.env.REACT_APP_API_URL || 'https://api.planivio.de';

// ✅ v19.0: Default View als Konstante für Analytics
const DEFAULT_VIEW = 'overview';

/**
 * Safe Track Function - nutzt /track/batch (existierender Endpoint!)
 * Identisch mit Assistent.js v14.1
 */
const trackAnalyticsEvent = async (token, eventName, metadata = {}) => {
  if (!token || !eventName) return;
  
  try {
    const event = {
      type: 'event',
      event_name: String(eventName).substring(0, 100),
      component: String(metadata.component || 'Budget').substring(0, 100),
      metadata: metadata,
      timestamp: new Date().toISOString()
    };
    
    const response = await fetch(`${API_URL}/api/analytics/track/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ events: [event] })
    });
    
    if (response.ok) {
      console.log(`✅ Analytics tracked: ${eventName}`, metadata);
    } else {
      console.warn(`❌ Analytics failed: ${response.status}`, eventName);
    }
  } catch (err) {
    console.warn(`❌ Analytics error: ${eventName}`, err.message);
  }
};

// ============================================================================
// CONSTANTS
// ============================================================================
const LOADING_DELAY_MS = 200;

// ============================================================================
// ✅ v18.3: DEDUPLICATE HELPER
// ============================================================================
const deduplicateById = (array) => {
  if (!Array.isArray(array)) return [];
  const seen = new Set();
  return array.filter(item => {
    if (!item?.id || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

// ============================================================================
// ✅ v17: GLOBALER STATE + SHARED PROMISE
// ============================================================================
let GLOBAL_CATEGORIES_CACHE = [];
let GLOBAL_TRANSACTIONS_CACHE = [];
let GLOBAL_LIMITS_CACHE = [];
let GLOBAL_SAVINGS_CACHE = [];
let GLOBAL_STATS_CACHE = null;
let GLOBAL_HAS_LOADED = false;
let GLOBAL_LOADING_PROMISE = null;

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function Budget({ active }) {
  // ✅ v25.2: useAuth statt useLogto (Native + Web)
  const { getAccessToken, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  
  // ============================================================================
  // STATE
  // ============================================================================
  const [view, setView] = useState(DEFAULT_VIEW);
  const [isLoading, setIsLoading] = useState(!GLOBAL_HAS_LOADED);
  const [showLoading, setShowLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(!GLOBAL_HAS_LOADED);
  
  // Period State
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  
  // Data States - Initialisiert aus Cache (mit Deduplizierung)
  const [categories, setCategories] = useState(() => deduplicateById(GLOBAL_CATEGORIES_CACHE));
  const [transactions, setTransactions] = useState(() => deduplicateById(GLOBAL_TRANSACTIONS_CACHE));
  const [limits, setLimits] = useState(() => deduplicateById(GLOBAL_LIMITS_CACHE));
  const [savingsGoals, setSavingsGoals] = useState(() => deduplicateById(GLOBAL_SAVINGS_CACHE));
  const [recurringItems, setRecurringItems] = useState([]);
  const [stats, setStats] = useState(() => GLOBAL_STATS_CACHE);
  
  // Modal States
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);

const [showContributionModal, setShowContributionModal] = useState(false);
  const tabBarRef = useRef(null); // ✅ v26.0: Chip-Leiste Scroll-Ref
  const [scrollEnd, setScrollEnd] = useState(false);

  // ✅ v26.0: Scroll-Ende erkennen (Fade-Effekt)
  const handleChipBarScroll = useCallback(() => {
    const el = tabBarRef.current;
    if (!el) return;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
    setScrollEnd(atEnd);
  }, []);

  // ✅ v26.0: Aktiven Chip ins Sichtfeld scrollen
  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    const activeChip = el.querySelector('.budget-chip.active');
    if (activeChip) {
      activeChip.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
    setTimeout(handleChipBarScroll, 100);
  }, [view, handleChipBarScroll]);

  const [editingCategory, setEditingCategory] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  
  // Filter States
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateRange, setDateRange] = useState('currentMonth');
  
  // Toast State
  const [toasts, setToasts] = useState([]);
  
  // Contribution Form
  const [contributionAmount, setContributionAmount] = useState('');

  // ============================================================================
  // REFS
  // ============================================================================
  const loadingTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const getAccessTokenRef = useRef(getAccessToken);
  const tokenRef = useRef(null);
  const hasTrackedViewRef = useRef(false); // ✅ v18.4: Prevent double tracking
  
  getAccessTokenRef.current = getAccessToken;

  // ============================================================================
  // ✅ v18.4: Track Mount/Unmount + Analytics
  // ============================================================================
  useEffect(() => {
    isMountedRef.current = true;
    console.log('🔄 Budget MOUNTED');

    return () => {
      isMountedRef.current = false;
      console.log('🔄 Budget UNMOUNTING');
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, []);

  // FAB Event
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.module === 'budget') {
        setShowTransactionModal(true);
      }
    };
    window.addEventListener('planivio-create-new', handler);
    return () => window.removeEventListener('planivio-create-new', handler);
  }, []);

  // ============================================================================
  // ✅ v19.0: Token holen UND Analytics component_view tracken
  // ✅ FIX: view aus Dependencies ENTFERNT
  // ============================================================================
  useEffect(() => {
    if (isAuthenticated && !tokenRef.current) {
      getAccessTokenRef.current(process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL)
        .then(token => { 
          tokenRef.current = token;
          console.log('🔐 Budget: Token acquired');
          
          // ✅ v19.0: Track component view NACH Token
          // initialView ist KONSTANT 'overview', nicht als Dependency
          if (!hasTrackedViewRef.current && active) {
            hasTrackedViewRef.current = true;
            trackAnalyticsEvent(token, 'component_view', { 
              component: 'Budget',
              initialView: DEFAULT_VIEW  // ✅ v19.0: Konstante statt State
            });
          }
        })
        .catch((err) => {
          console.warn('🔐 Budget: Token failed', err.message);
        });
    }
  }, [isAuthenticated, active]); // ✅ v19.0: OHNE view - verhindert Re-Run bei Tab-Wechsel

  // ============================================================================
  // ✅ v18.4: ANALYTICS HELPER - Track mit aktuellem Token
  // ============================================================================
  const trackEvent = useCallback(async (eventName, metadata = {}) => {
    // Token aus Ref oder neu holen
    let token = tokenRef.current;
    
    if (!token) {
      try {
        token = await getAccessTokenRef.current(process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL);
        tokenRef.current = token;
      } catch (err) {
        console.warn('❌ Could not get token for analytics');
        return;
      }
    }
    
    trackAnalyticsEvent(token, eventName, {
      component: 'Budget',
      ...metadata
    });
  }, []);

  // ============================================================================
  // ✅ v18.4: Track Tab/View Changes
  // ============================================================================
  const handleViewChange = useCallback((newView) => {
    setView(newView);
    
    // Track tab change
    trackEvent('budget_tab_change', {
      from_view: view,
      to_view: newView
    });
  }, [view, trackEvent]);

  // ============================================================================
  // Sync to Global Cache (mit Deduplizierung)
  // ============================================================================
  useEffect(() => {
    if (categories.length > 0) GLOBAL_CATEGORIES_CACHE = deduplicateById(categories);
    if (transactions.length > 0) GLOBAL_TRANSACTIONS_CACHE = deduplicateById(transactions);
    if (limits.length > 0) GLOBAL_LIMITS_CACHE = deduplicateById(limits);
    if (savingsGoals.length > 0) GLOBAL_SAVINGS_CACHE = deduplicateById(savingsGoals);
    if (stats) GLOBAL_STATS_CACHE = stats;
  }, [categories, transactions, limits, savingsGoals, stats]);

  // ==================== CONSTANTS ====================
  const months = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  // ==================== TOAST SYSTEM ====================
  const showToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);

  // ============================================================================
  // FETCH FUNCTION
  // ============================================================================
  const fetchDataFromBackend = useCallback(async () => {
    console.log('📡 Fetching budget data from backend...');
    
    const token = await getAccessTokenRef.current(process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL);
    tokenRef.current = token; // ✅ v18.4: Store token
    
    if (!token) {
      throw new Error('Kein Token erhalten');
    }
    
    const [data, recurringData] = await Promise.all([
      budgetService.loadAllData(token),
      budgetService.getRecurringTransactions(token).catch(() => ({ recurring: [] }))
    ]);

    let savingsData = [];
    try {
      const savings = await budgetService.getSavingsGoals(token);
      savingsData = savings.savings || [];
    } catch (e) {
      console.log('Savings goals not available yet');
    }

    console.log('✅ Fetched budget data:', {
      categories: data.categories?.length || 0,
      transactions: data.transactions?.length || 0,
      limits: data.limits?.length || 0,
      recurring: recurringData.recurring?.length || 0,
      savings: savingsData.length
    });

    return {
      categories: data.categories || [],
      transactions: data.transactions || [],
      recurring: recurringData.recurring || [],
      limits: data.limits || [],
      stats: data.stats || {},
      savingsGoals: savingsData
    };
  }, []);

  // ============================================================================
  // LOAD DATA - MIT SHARED PROMISE
  // ============================================================================
  const loadData = useCallback(async (forceRefresh = false) => {
    // Bereits gecached und kein Force-Refresh?
    if (GLOBAL_HAS_LOADED && !forceRefresh && (
      GLOBAL_CATEGORIES_CACHE.length > 0 || 
      GLOBAL_TRANSACTIONS_CACHE.length > 0
    )) {
      console.log('📦 Using cached budget data');
      if (isMountedRef.current) {
        setCategories(deduplicateById(GLOBAL_CATEGORIES_CACHE));
        setTransactions(deduplicateById(GLOBAL_TRANSACTIONS_CACHE));
        setLimits(deduplicateById(GLOBAL_LIMITS_CACHE));
        setSavingsGoals(deduplicateById(GLOBAL_SAVINGS_CACHE));
        setStats(GLOBAL_STATS_CACHE);
        setIsLoading(false);
        setIsInitialLoad(false);
      }
      return;
    }
    
    // Wenn bereits ein Load läuft, warte auf dasselbe Promise!
    if (GLOBAL_LOADING_PROMISE) {
      console.log('⏳ Waiting for existing load...');
      try {
        const result = await GLOBAL_LOADING_PROMISE;
        console.log('✅ Got data from shared promise');
        if (isMountedRef.current) {
          setCategories(deduplicateById(result.categories));
          setTransactions(deduplicateById(result.transactions));
          setRecurringItems(result.recurring || []);
          setLimits(deduplicateById(result.limits));
          setSavingsGoals(deduplicateById(result.savingsGoals));
          setStats(result.stats);
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      } catch (err) {
        console.error('❌ Shared promise failed:', err);
        if (isMountedRef.current) {
          showToast(t('common.error') + ': ' + err.message, 'error');
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      }
      return;
    }
    
    // Neues Loading starten
    console.log('🚀 Starting new load...');
    
    if (isMountedRef.current) {
      setIsLoading(true);
      
      // Delayed loading indicator
      if (!isInitialLoad) {
        loadingTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setShowLoading(true);
          }
        }, LOADING_DELAY_MS);
      }
    }
    
    // Shared Promise erstellen
    GLOBAL_LOADING_PROMISE = fetchDataFromBackend();
    
    try {
      const result = await GLOBAL_LOADING_PROMISE;
      
      // Update Global Cache with deduplication
      GLOBAL_CATEGORIES_CACHE = deduplicateById(result.categories);
      GLOBAL_TRANSACTIONS_CACHE = deduplicateById(result.transactions);
      GLOBAL_LIMITS_CACHE = deduplicateById(result.limits);
      GLOBAL_SAVINGS_CACHE = deduplicateById(result.savingsGoals);
      GLOBAL_STATS_CACHE = result.stats;
      GLOBAL_HAS_LOADED = true;
      
      // Update State with deduplication
      if (isMountedRef.current) {
        setCategories(deduplicateById(result.categories));
        setTransactions(deduplicateById(result.transactions));
        setLimits(deduplicateById(result.limits));
        setSavingsGoals(deduplicateById(result.savingsGoals));
        setStats(result.stats);
        setIsInitialLoad(false);
      }
      
    } catch (err) {
      console.error('❌ Load error:', err.message);
      
      // ✅ v18.4: Track error
      trackEvent('budget_load_error', { error: err.message });
      
      if (isMountedRef.current) {
        showToast(t('common.error') + ': ' + err.message, 'error');
        setIsInitialLoad(false);
        
        // Bei Fehler: Cache verwenden falls vorhanden
        if (GLOBAL_CATEGORIES_CACHE.length > 0) {
          setCategories(deduplicateById(GLOBAL_CATEGORIES_CACHE));
          setTransactions(deduplicateById(GLOBAL_TRANSACTIONS_CACHE));
          setLimits(deduplicateById(GLOBAL_LIMITS_CACHE));
          setSavingsGoals(deduplicateById(GLOBAL_SAVINGS_CACHE));
          setStats(GLOBAL_STATS_CACHE);
        }
      }
    } finally {
      GLOBAL_LOADING_PROMISE = null;
      
      if (isMountedRef.current) {
        setIsLoading(false);
        setShowLoading(false);
      }
      
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }
  }, [isInitialLoad, fetchDataFromBackend, showToast, t, trackEvent]);

  // ============================================================================
  // INITIAL LOAD
  // ============================================================================
  useEffect(() => {
    if (!active) return;
    
    // Cache vorhanden?
    if (GLOBAL_HAS_LOADED && (
      GLOBAL_CATEGORIES_CACHE.length > 0 || 
      GLOBAL_TRANSACTIONS_CACHE.length > 0
    )) {
      console.log('📦 Using global cache on mount');
      setCategories(deduplicateById(GLOBAL_CATEGORIES_CACHE));
      setTransactions(deduplicateById(GLOBAL_TRANSACTIONS_CACHE));
      setLimits(deduplicateById(GLOBAL_LIMITS_CACHE));
      setSavingsGoals(deduplicateById(GLOBAL_SAVINGS_CACHE));
      setStats(GLOBAL_STATS_CACHE);
      setIsLoading(false);
      setIsInitialLoad(false);
      return;
    }
    
    if (isAuthLoading) {
      console.log('⏳ Auth loading...');
      return;
    }
    
    if (!isAuthenticated) {
      console.log('🔒 Not authenticated');
      setIsLoading(false);
      setIsInitialLoad(false);
      return;
    }
    
    console.log('🎬 Starting initial load');
    loadData();
  }, [active, isAuthenticated, isAuthLoading, loadData]);

  // ============================================================================
  // REFRESH HANDLERS
  // ============================================================================
const refreshData = useCallback(async () => {
    try {
      const token = await getAccessTokenRef.current(process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL);
      tokenRef.current = token; // ✅ v18.4: Update token ref
      const data = await budgetService.loadAllData(token);
      
      const updateCategories = data.categories || [];
      const updateTransactions = data.transactions || [];
      const updateLimits = data.limits || [];
      const updateStats = data.stats || {};
      
      let updateSavings = [];
      try {
        const savingsData = await budgetService.getSavingsGoals(token);
        updateSavings = savingsData.savings || [];
      } catch (e) {}
      
      // Update Cache with deduplication
      GLOBAL_CATEGORIES_CACHE = deduplicateById(updateCategories);
      GLOBAL_TRANSACTIONS_CACHE = deduplicateById(updateTransactions);
      GLOBAL_LIMITS_CACHE = deduplicateById(updateLimits);
      GLOBAL_SAVINGS_CACHE = deduplicateById(updateSavings);
      GLOBAL_STATS_CACHE = updateStats;
      
      if (isMountedRef.current) {
        setCategories(deduplicateById(updateCategories));
        setTransactions(deduplicateById(updateTransactions));
        setLimits(deduplicateById(updateLimits));
        setSavingsGoals(deduplicateById(updateSavings));
        setStats(updateStats);
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  }, []);

  // ==================== PERIOD NAVIGATION ====================
  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(prev => prev - 1);
    } else {
      setCurrentMonth(prev => prev - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(prev => prev + 1);
    } else {
      setCurrentMonth(prev => prev + 1);
    }
  };

  // ==================== HANDLERS - ✅ v18.4: Mit Analytics ====================
  
  const setupDefaultCategories = async () => {
    if (!window.confirm(t('budget.confirmSetupCategories'))) return;
    
    setIsLoading(true);
    try {
      const token = await getAccessTokenRef.current(process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL);
      const created = await budgetService.createDefaultCategories(token);
      await refreshData();
      showToast(`✅ ${created.length} ${t('budget.categoriesCreated')}`, 'success');
      
      // ✅ v18.4: Track
      trackEvent('budget_default_categories_created', { count: created.length });
    } catch (error) {
      showToast('❌ ' + t('common.error') + ': ' + error.message, 'error');
    }
    setIsLoading(false);
  };

  // ✅ v18.4: Contribution mit Analytics
  const handleContribution = async () => {
    if (!selectedGoal || !contributionAmount) return;

    try {
      const token = await getAccessTokenRef.current(process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL);
      await budgetService.contributeSavings(token, selectedGoal.id, {
        amount: parseFloat(contributionAmount)
      });
      
      setShowContributionModal(false);
      setContributionAmount('');
      showToast(`✅ ${contributionAmount} € eingezahlt`, 'success');
      await refreshData();
      
      // ✅ v18.4: Track contribution
      trackEvent('budget_contribution', {
        goal_id: selectedGoal.id,
        goal_name: selectedGoal.name,
        amount: parseFloat(contributionAmount)
      });
    } catch (error) {
      showToast('❌ ' + error.message, 'error');
    }
  };

  const openContributionModal = (goal) => {
    setSelectedGoal(goal);
    setContributionAmount(goal.monthly_contribution?.toString() || '');
    setShowContributionModal(true);
  };

  // ==================== CALCULATIONS ====================
  // Echte Transaktionen für den ausgewählten Monat
  const monthTransactions = transactions.filter(tx => {
    const txDate = new Date(tx.date);
    return txDate.getMonth() === currentMonth && txDate.getFullYear() === currentYear;
  });

  // ✅ Virtuelle Recurring-Einträge für den Monat generieren
  const existingDescriptions = new Set(monthTransactions.map(tx => tx.description));
  const virtualRecurring = recurringItems
    .filter(rt => rt.isActive || rt.is_active)
    .filter(rt => (rt.frequency === 'monthly' || rt.frequency === 'weekly'))
    .filter(rt => !existingDescriptions.has(rt.description))
    .map(rt => {
      const day = Math.min(rt.day_of_month || 1, 28);
      return {
        id: `recurring-${rt.id}-${currentYear}-${currentMonth + 1}`,
        amount: parseFloat(rt.amount),
        category: rt.category,
        type: rt.type,
        description: rt.description,
        date: `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        member_id: rt.member_id || rt.memberId,
        member_name: rt.member_name || rt.memberName,
        category_icon: rt.category_icon || rt.categoryIcon,
        category_color: rt.category_color || rt.categoryColor,
        is_recurring: true,
        is_virtual: true,
      };
    });

  const allMonthTransactions = [...monthTransactions, ...virtualRecurring];

  const filteredTransactions = allMonthTransactions.filter(tx => {
    if (filterType !== 'all' && tx.type !== filterType) return false;
    if (filterCategory !== 'all' && tx.category !== filterCategory) return false;
    return true;
  });

  const summary = {
    income: filteredTransactions.filter(tx => tx.type === 'income').reduce((sum, tx) => sum + parseFloat(tx.amount), 0),
    expense: filteredTransactions.filter(tx => tx.type === 'expense').reduce((sum, tx) => sum + Math.abs(parseFloat(tx.amount)), 0),
    balance: 0,
    savingsTotal: savingsGoals.reduce((sum, g) => sum + (parseFloat(g.current_amount) || 0), 0)
  };
  summary.balance = summary.income - summary.expense;

  const filteredCategories = categories.filter(cat => {
    if (categoryFilter === 'all') return true;
    if (categoryFilter === 'fixed') return cat.is_fixed;
    if (categoryFilter === 'variable') return !cat.is_fixed;
    return true;
  });

  // ==================== HELPERS ====================
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.abs(amount || 0));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  const getCategoryProgress = (categoryName) => {
    const limit = limits.find(l => l.category === categoryName);
    if (!limit) return null;
    
    const spent = filteredTransactions
      .filter(tx => tx.category === categoryName && tx.type === 'expense')
      .reduce((sum, tx) => sum + Math.abs(parseFloat(tx.amount)), 0);
    
    return {
      spent,
      limit: parseFloat(limit.amount),
      percentage: Math.round((spent / parseFloat(limit.amount)) * 100)
    };
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return '#EF4444';
    if (percentage >= 80) return '#F59E0B';
    return '#22C55E';
  };

  const getStatusIcon = (percentage) => {
    if (percentage >= 100) return { icon: '⚠️', class: 'danger' };
    if (percentage >= 80) return { icon: '⚡', class: 'warning' };
    return { icon: '✓', class: 'ok' };
  };

  const getDaysRemaining = () => {
    const now = new Date();
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    return Math.max(0, lastDay.getDate() - now.getDate());
  };

  const getDailyBudget = () => {
    const days = getDaysRemaining();
    return days > 0 ? summary.balance / days : 0;
  };

  // ==================== RENDER GUARDS ====================
  if (!active) return null;
  
  if (!isAuthenticated && !isAuthLoading) {
    return (
      <div className={`budget-container ${isDark ? 'dark' : ''}`}>
        <div className="budget-auth-required">
          <span style={{ fontSize: '3rem' }}>🔒</span>
          <p>{t('budget.pleaseLogin')}</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: SKELETON UI
  // ============================================================================
  if (isInitialLoad && categories.length === 0 && transactions.length === 0) {
    return (
      <div className={`budget-container ${isDark ? 'dark' : ''}`}>
        {/* Header Skeleton */}
        <div className="budget-header-row1">
          <div className="budget-header-left">
            <Skeleton width={44} height={44} borderRadius={12} />
            <div className="budget-header-title-wrap">
              <Skeleton width={100} height={20} style={{ marginBottom: 6 }} />
              <Skeleton width={160} height={14} />
            </div>
          </div>
          <Skeleton width={44} height={44} borderRadius={12} />
        </div>

        {/* Tabs Skeleton */}
        <div className="budget-tabs-container">
          <Skeleton width={180} height={40} borderRadius={12} />
        </div>

        {/* Summary Cards Skeleton */}
        <div className="budget-content">
          <div className="summary-grid">
            {[1,2,3,4].map(i => (
              <div key={i} className="summary-card" style={{ background: isDark ? '#1F2937' : '#fff' }}>
                <Skeleton width={40} height={40} borderRadius={10} style={{ marginBottom: 12 }} />
                <Skeleton width={80} height={16} style={{ marginBottom: 8 }} />
                <Skeleton width={100} height={28} />
              </div>
            ))}
          </div>
          
          {/* Content Skeleton */}
          <div className="overview-grid" style={{ marginTop: '1.5rem' }}>
            <div className="overview-left">
              <Skeleton width="100%" height={300} borderRadius={16} />
            </div>
            <div className="overview-right">
              <Skeleton width="100%" height={200} borderRadius={16} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Auth Loading
  if (isAuthLoading) {
    return (
      <div className={`budget-container ${isDark ? 'dark' : ''}`}>
        <Loading icon="🔐" text="Authentifizierung..." />
      </div>
    );
  }

  // ==================== TAB CONFIG - ✅ v26.0: Scrollbare Chip-Leiste ====================
  const tabs = [
    { id: 'overview', icon: '📊', label: 'Übersicht' },
    { id: 'transactions', icon: '📝', label: 'Transaktionen' },
    { id: 'savings', icon: '🎯', label: 'Sparziele' },
    { id: 'recurring', icon: '🔄', label: 'Daueraufträge' },
    { id: 'debts', icon: '💳', label: 'Schulden' },
    { id: 'categories', icon: '🏷️', label: 'Kategorien' },
    { id: 'limits', icon: '📈', label: 'Limits' }
  ];

  // ==================== RENDER ====================
  return (
    <div className={`budget-container ${isDark ? 'dark' : ''}`}>
      
      {/* Loading Indicator */}
      {showLoading && (
        <div className="budget-loading-indicator">
          <span className="loading-spinner">⏳</span>
          <span>Aktualisiere...</span>
        </div>
      )}

      {/* ==================== HEADER ==================== */}
      <div className="budget-header-row1">
        <div className="budget-header-left">
          <div className="budget-header-icon">💰</div>
          <div className="budget-header-title-wrap">
            <h1 className="budget-header-title">{t('budget.title')}</h1>
            <span className="budget-header-subtitle">
              {categories.length} Kategorien, {transactions.length} Transaktionen
              {isLoading && !showLoading && (
                <span className="sync-indicator">
                  <span className="sync-dot" />
                  sync
                </span>
              )}
            </span>
          </div>
          <button
            className="budget-info-btn"
            onClick={() => setShowInfo(!showInfo)}
            title="Modul-Info"
          >
            ⓘ
          </button>
        </div>
      </div>

      {/* Period Selector — eigene Zeile */}
      <div className="budget-period-row">
        <div className="period-selector">
          <button className="period-btn" onClick={prevMonth}>◀</button>
          <span className="period-text">{months[currentMonth]} {currentYear}</span>
          <button className="period-btn" onClick={nextMonth}>▶</button>
        </div>
      </div>

      {/* Info Modal */}
      {showInfo && (
        <div className="info-modal">
          <div className="info-modal-header">
            <span className="info-modal-title">💰 Budget & Finanzen - Info</span>
            <button className="info-close-btn" onClick={() => setShowInfo(false)}>✕</button>
          </div>
          <div className="info-modal-content">
            <p className="info-text">
              Verwalte dein Haushaltsgeld, tracke Einnahmen und Ausgaben und behalte deine Sparziele im Blick.
            </p>
            <ul className="info-list">
              <li>💵 Einnahmen und Ausgaben erfassen</li>
              <li>📋 Kategorien mit Budgetlimits verwalten</li>
              <li>🎯 Sparziele erstellen und verfolgen</li>
              <li>🔄 Wiederkehrende Transaktionen automatisieren</li>
              <li>🏦 Bankimport für schnelle Buchungen</li>
              <li>📊 Monatsübersicht mit Auswertungen</li>
            </ul>
          </div>
        </div>
      )}

      {/* ==================== TABS - ✅ v26.0: Scrollbare Chip-Leiste ==================== */}
      <div className={`budget-tabs-container ${scrollEnd ? 'scroll-end' : ''}`}>
        <div
          className="budget-chip-bar"
          ref={tabBarRef}
          onScroll={handleChipBarScroll}
        >
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleViewChange(tab.id)}
              className={`budget-chip ${view === tab.id ? 'active' : ''}`}
            >
              <span className="chip-icon">{tab.icon}</span>
              <span className="chip-label">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ==================== CONTENT ==================== */}
      <div className="budget-content" style={{
        opacity: showLoading ? 0.7 : 1,
        transition: 'opacity 0.2s ease',
        pointerEvents: showLoading ? 'none' : 'auto'
      }}>
        
        {/* ==================== OVERVIEW ==================== */}
        {view === 'overview' && (
          <div className="budget-overview">
            
            {/* Summary Cards */}
            <div className="summary-grid">
              <div className="summary-card income">
                <div className="summary-icon">💵</div>
                <div className="summary-label">{t('budget.income')}</div>
                <div className="summary-value">{formatCurrency(summary.income)}</div>
                <div className="summary-meta">
                  <span className="trend up">✓ Erhalten</span>
                </div>
              </div>
              
              <div className="summary-card expense">
                <div className="summary-icon">💸</div>
                <div className="summary-label">{t('budget.expenses')}</div>
                <div className="summary-value">{formatCurrency(summary.expense)}</div>
                <div className="summary-meta">
                  <span className="trend">{filteredTransactions.filter(t => t.type === 'expense').length} Ausgaben</span>
                </div>
              </div>
              
              <div className="summary-card balance">
                <div className="summary-icon">📊</div>
                <div className="summary-label">Verfügbar</div>
                <div className="summary-value">{formatCurrency(summary.balance)}</div>
                <div className="summary-meta">
                  <span>Noch {getDaysRemaining()} Tage</span>
                  <span className="trend">~{formatCurrency(getDailyBudget())}/Tag</span>
                </div>
              </div>
              
              <div className="summary-card savings">
                <div className="summary-icon">🎯</div>
                <div className="summary-label">Gespart</div>
                <div className="summary-value">{formatCurrency(summary.savingsTotal)}</div>
                <div className="summary-meta">
                  <span>{savingsGoals.length} Sparziele</span>
                </div>
              </div>
            </div>

            {/* Content Grid */}
            <div className="overview-grid">
              {/* Left Column - Categories */}
              <div className="overview-left">
                <div className="card">
                  <div className="card-header">
                    <h2 className="card-title">
                      <span>📋</span> Budget nach Kategorien
                    </h2>
                  </div>
                  <div className="category-tabs">
                    <button 
                      className={`cat-tab ${categoryFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setCategoryFilter('all')}
                    >
                      Alle
                    </button>
                    <button 
                      className={`cat-tab ${categoryFilter === 'fixed' ? 'active' : ''}`}
                      onClick={() => setCategoryFilter('fixed')}
                    >
                      Fixkosten
                    </button>
                    <button 
                      className={`cat-tab ${categoryFilter === 'variable' ? 'active' : ''}`}
                      onClick={() => setCategoryFilter('variable')}
                    >
                      Variable
                    </button>
                  </div>
                  <div className="category-list">
                    {filteredCategories.length === 0 ? (
                      <div className="empty-state">
                        <p>Keine Kategorien gefunden</p>
                        <button className="btn-primary" onClick={setupDefaultCategories}>
                          ✨ Standard-Kategorien erstellen
                        </button>
                      </div>
                    ) : (
                      filteredCategories.slice(0, 7).map(cat => {
                        const progress = getCategoryProgress(cat.name);
                        const percentage = progress ? progress.percentage : 0;
                        const status = getStatusIcon(percentage);
                        
                        return (
                          <div key={cat.id} className="category-item">
                            <div className="category-icon" style={{ background: `${cat.color || '#4A90A4'}20` }}>
                              {cat.icon || '📁'}
                            </div>
                            <div className="category-info">
                              <div className="category-name">
                                {cat.name}
                                <span className={`category-tag ${cat.is_fixed ? 'fixed' : 'variable'}`}>
                                  {cat.is_fixed ? 'Fix' : 'Var'}
                                </span>
                              </div>
                              {progress && (
                                <div className="category-progress">
                                  <div 
                                    className="category-progress-bar"
                                    style={{ 
                                      width: `${Math.min(percentage, 100)}%`,
                                      background: getProgressColor(percentage)
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                            {progress && (
                              <>
                                <div className="category-amounts">
                                  <div className="category-spent" style={{ color: percentage >= 100 ? '#EF4444' : 'inherit' }}>
                                    {formatCurrency(progress.spent)}
                                  </div>
                                  <div className="category-budget">
                                    von {formatCurrency(progress.limit)}
                                  </div>
                                </div>
                                <div className={`category-status ${status.class}`}>
                                  {status.icon}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Recent Transactions */}
                <div className="card">
                  <div className="card-header">
                    <h2 className="card-title">
                      <span>📝</span> Letzte Transaktionen
                    </h2>
                    <button className="btn-secondary" onClick={() => handleViewChange('transactions')}>
                      Alle anzeigen
                    </button>
                  </div>
                  <div className="transaction-list">
                    {filteredTransactions.length === 0 ? (
                      <div className="empty-state">
                        <p>Keine Transaktionen in diesem Monat</p>
                      </div>
                    ) : (
                      filteredTransactions.slice(0, 6).map(tx => (
                        <div key={tx.id} className="transaction-item">
                          <div className="transaction-icon" style={{ background: `${tx.category_color || '#4A90A4'}20` }}>
                            {tx.category_icon || '💳'}
                          </div>
                          <div className="transaction-info">
                            <div className="transaction-title">{tx.description || tx.category}</div>
                            <div className="transaction-meta">
                              <span>{formatDate(tx.date)}</span>
                              {tx.is_recurring && (
                                <span className="transaction-source">🔄 Wiederkehrend</span>
                              )}
                            </div>
                          </div>
                          <div className={`transaction-amount ${tx.type}`}>
                            {tx.type === 'income' ? '+' : '-'}{formatCurrency(Math.abs(tx.amount))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Quick Overview */}
              <div className="overview-right">
                {/* Quick Links */}
                <div className="card">
                  <div className="card-header">
                    <h2 className="card-title">
                      <span>⚡</span> Schnellzugriff
                    </h2>
                  </div>
                  <div className="quick-links">
                    <button className="quick-link" onClick={() => handleViewChange('savings')}>
                      <span className="ql-icon">🎯</span>
                      <span className="ql-label">Sparziele</span>
                      <span className="ql-count">{savingsGoals.length}</span>
                    </button>
                    <button className="quick-link" onClick={() => handleViewChange('recurring')}>
                      <span className="ql-icon">🔄</span>
                      <span className="ql-label">Daueraufträge</span>
                      <span className="ql-arrow">→</span>
                    </button>
                    <button className="quick-link" onClick={() => handleViewChange('debts')}>
                      <span className="ql-icon">💳</span>
                      <span className="ql-label">Schulden</span>
                      <span className="ql-arrow">→</span>
                    </button>
                    <button className="quick-link" onClick={() => handleViewChange('limits')}>
                      <span className="ql-icon">📈</span>
                      <span className="ql-label">Limits</span>
                      <span className="ql-count">{limits.length}</span>
                    </button>
                  </div>
                </div>

                {/* Savings Summary */}
                {savingsGoals.length > 0 && (
                  <div className="card">
                    <div className="card-header">
                      <h2 className="card-title">
                        <span>🎯</span> Sparziele
                      </h2>
                      <button className="btn-text" onClick={() => handleViewChange('savings')}>
                        Alle →
                      </button>
                    </div>
                    <div className="savings-preview">
                      {savingsGoals.slice(0, 2).map(goal => (
                        <div key={goal.id} className="savings-preview-item">
                          <div className="sp-icon">{goal.icon || '🎯'}</div>
                          <div className="sp-info">
                            <div className="sp-name">{goal.name}</div>
                            <div className="sp-progress">
                              <div 
                                className="sp-bar"
                                style={{ 
                                  width: `${Math.min(goal.percentage || 0, 100)}%`,
                                  background: goal.color || '#4A90A4'
                                }}
                              />
                            </div>
                          </div>
                          <div className="sp-percentage">{goal.percentage || 0}%</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ==================== TRANSACTIONS - ✅ v18.4: Mit Analytics ==================== */}
        {view === 'transactions' && (
          <BudgetTransactions 
            transactions={filteredTransactions}
            categories={categories}
            onAdd={() => {
              setEditingTransaction(null);
              setShowTransactionModal(true);
            }}
            onEdit={(tx) => {
              setEditingTransaction(tx);
              setShowTransactionModal(true);
            }}
            onDelete={async (id) => {
              if (!window.confirm(t('budget.confirmDeleteTransaction'))) return;
              try {
                const token = await getAccessTokenRef.current(process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL);
                await budgetService.deleteTransaction(token, id);
                await refreshData();
                showToast('✅ Transaktion gelöscht', 'success');
                
                // ✅ v18.4: Track delete
                trackEvent('budget_transaction_delete', { transaction_id: id });
              } catch (error) {
                showToast('❌ ' + error.message, 'error');
              }
            }}
            filterType={filterType}
            setFilterType={setFilterType}
            filterCategory={filterCategory}
            setFilterCategory={setFilterCategory}
            dateRange={dateRange}
            setDateRange={setDateRange}
          />
        )}

        {/* ==================== SAVINGS ==================== */}
        {view === 'savings' && (
          <SavingsGoals active={true} />
        )}

        {/* ==================== RECURRING ==================== */}
        {view === 'recurring' && (
          <RecurringTransactions active={true} />
        )}

        {/* ==================== DEBTS ==================== */}
        {view === 'debts' && (
          <Debts active={true} />
        )}

        {/* ==================== CATEGORIES - ✅ v18.4: Mit Analytics ==================== */}
        {view === 'categories' && (
          <div>
            {categories.length === 0 && (
              <div className="setup-box">
                <div className="setup-icon">🚀</div>
                <h3>{t('budget.noCategories')}</h3>
                <p>{t('budget.setupCategoriesHint')}</p>
                <button onClick={setupDefaultCategories} className="btn-primary">
                  <span>✨</span> {t('budget.setupCategories')}
                </button>
              </div>
            )}
            
            <BudgetCategories
              categories={categories}
              onAdd={() => { setEditingCategory(null); setShowCategoryModal(true); }}
              onEdit={(cat) => {
                setEditingCategory(cat);
                setShowCategoryModal(true);
              }}
              onDelete={async (id) => {
                if (!window.confirm(t('budget.confirmDeleteCategory'))) return;
                try {
                  const token = await getAccessTokenRef.current(process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL);
                  await budgetService.deleteCategory(token, id);
                  await refreshData();
                  showToast('✅ Kategorie gelöscht', 'success');
                  
                  // ✅ v18.4: Track delete
                  trackEvent('budget_category_delete', { category_id: id });
                } catch (error) {
                  showToast('❌ ' + error.message, 'error');
                }
              }}
            />
          </div>
        )}

        {/* ==================== LIMITS - ✅ v18.4: Mit Analytics ==================== */}
        {view === 'limits' && (
          <BudgetLimits
            limits={limits}
            categories={categories}
            transactions={transactions}
            onAdd={() => setShowLimitModal(true)}
            onDelete={async (id) => {
              if (!window.confirm(t('budget.confirmDeleteLimit'))) return;
              try {
                const token = await getAccessTokenRef.current(process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL);
                await budgetService.deleteLimit(token, id);
                await refreshData();
                showToast('✅ Limit gelöscht', 'success');
                
                // ✅ v18.4: Track delete
                trackEvent('budget_limit_delete', { limit_id: id });
              } catch (error) {
                showToast('❌ ' + error.message, 'error');
              }
            }}
          />
        )}

        {/* ==================== TRENDS & BANK-IMPORT: entfernt in v26.0 (Tabs kommen zurück wenn Feature fertig) ==================== */}
      </div>

      {/* ==================== MODALS - ✅ v18.4: Mit Analytics ==================== */}
      <CreateCategoryModal
        show={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setEditingCategory(null);
        }}
        editing={editingCategory}
        onSave={async (data) => {
          try {
            const token = await getAccessTokenRef.current(process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL);
            if (editingCategory) {
              await budgetService.updateCategory(token, editingCategory.id, data);
              // ✅ v18.4: Track edit
              trackEvent('budget_category_edit', { 
                category_id: editingCategory.id,
                category_name: data.name 
              });
            } else {
              await budgetService.createCategory(token, data);
              // ✅ v18.4: Track create
              trackEvent('budget_category_create', { category_name: data.name });
            }
            await refreshData();
            setShowCategoryModal(false);
            setEditingCategory(null);
            showToast('✅ Kategorie gespeichert', 'success');
          } catch (error) {
            showToast('❌ ' + error.message, 'error');
          }
        }}
        onDelete={async () => {
          if (!window.confirm(t('budget.confirmDeleteCategory'))) return;
          try {
            const token = await getAccessTokenRef.current(process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL);
            await budgetService.deleteCategory(token, editingCategory.id);
            await refreshData();
            setShowCategoryModal(false);
            setEditingCategory(null);
            showToast('✅ Kategorie gelöscht', 'success');
            
            // ✅ v18.4: Track delete
            trackEvent('budget_category_delete', { category_id: editingCategory.id });
          } catch (error) {
            showToast('❌ ' + error.message, 'error');
          }
        }}
      />

      <CreateTransactionModal
        show={showTransactionModal}
        onClose={() => {
          setShowTransactionModal(false);
          setEditingTransaction(null);
        }}
        editing={editingTransaction}
        categories={categories}
        onSave={async (data) => {
          try {
            const token = await getAccessTokenRef.current(process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL);
            if (editingTransaction) {
              await budgetService.updateTransaction(token, editingTransaction.id, data);
              // ✅ v18.4: Track edit
              trackEvent('budget_transaction_edit', { 
                transaction_id: editingTransaction.id,
                type: data.type,
                category: data.category,
                amount: data.amount
              });
            } else {
              await budgetService.createTransaction(token, data);
              // ✅ v18.4: Track create
              trackEvent('budget_transaction_create', { 
                type: data.type,
                category: data.category,
                amount: data.amount
              });
            }
            await refreshData();
            setShowTransactionModal(false);
            setEditingTransaction(null);
            showToast('✅ Transaktion gespeichert', 'success');
          } catch (error) {
            showToast('❌ ' + error.message, 'error');
          }
        }}
        onDelete={async () => {
          if (!window.confirm(t('budget.confirmDeleteTransaction'))) return;
          try {
            const token = await getAccessTokenRef.current(process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL);
            await budgetService.deleteTransaction(token, editingTransaction.id);
            await refreshData();
            setShowTransactionModal(false);
            setEditingTransaction(null);
            showToast('✅ Transaktion gelöscht', 'success');
            
            // ✅ v18.4: Track delete
            trackEvent('budget_transaction_delete', { transaction_id: editingTransaction.id });
          } catch (error) {
            showToast('❌ ' + error.message, 'error');
          }
        }}
      />

      <CreateLimitModal
        show={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        categories={categories}
        onSave={async (data) => {
          try {
            const token = await getAccessTokenRef.current(process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL);
            await budgetService.createLimit(token, data);
            await refreshData();
            setShowLimitModal(false);
            showToast('✅ Limit erstellt', 'success');
            
            // ✅ v18.4: Track create
            trackEvent('budget_limit_create', { 
              category: data.category,
              amount: data.amount
            });
          } catch (error) {
            showToast('❌ ' + error.message, 'error');
          }
        }}
      />

      {/* Contribution Modal */}
      {showContributionModal && selectedGoal && (
        <div className="modal-overlay" onClick={() => setShowContributionModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>💰 Einzahlung</h3>
              <button className="modal-close" onClick={() => setShowContributionModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="contribution-preview">
                <div className="preview-icon">{selectedGoal.icon || '🎯'}</div>
                <div className="preview-name">{selectedGoal.name}</div>
                <div className="preview-progress">
                  {formatCurrency(selectedGoal.current_amount)} € / {formatCurrency(selectedGoal.target_amount)} €
                </div>
              </div>

              <div className="form-group">
                <label>Betrag</label>
                <div className="input-with-suffix">
                  <input 
                    type="number"
                    placeholder="0"
                    value={contributionAmount}
                    onChange={e => setContributionAmount(e.target.value)}
                    autoFocus
                  />
                  <span className="suffix">€</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowContributionModal(false)}>
                Abbrechen
              </button>
              <button className="btn-primary" onClick={handleContribution} disabled={!contributionAmount}>
                💰 Einzahlen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== TOASTS ==================== */}
      <div className="toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast ${toast.type}`}>
            {toast.message}
          </div>
        ))}
      </div>
      
      {/* ============================================================================
       * PLANIVIO BRANDING CSS
       * ============================================================================ */}
      <style>{`
        /* ========== PLANIVIO FARBEN ========== */
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        /* ========== SYNC INDICATOR ========== */
        .sync-indicator {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-left: 8px;
          font-size: 0.75rem;
          color: #4A90A4;
        }
        .sync-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #4A90A4;
          animation: pulse 1s infinite;
        }
        
        /* ========== LOADING INDICATOR ========== */
        .budget-loading-indicator {
          position: fixed;
          top: 80px;
          right: 20px;
          background: linear-gradient(135deg, #4A90A4 0%, #6BA893 100%);
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          gap: 8px;
          z-index: 1000;
          box-shadow: 0 4px 12px rgba(74, 144, 164, 0.3);
        }
        .loading-spinner {
          animation: pulse 1s infinite;
        }
        
        /* ========== HEADER ========== */
        .header-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .refresh-btn {
          padding: 10px;
          background: white;
          border: 2px solid #B8D4DC;
          border-radius: 10px;
          font-size: 1rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }
        .refresh-btn:hover:not(:disabled) {
          border-color: #4A90A4;
          background: #E8F4F8;
        }
        .refresh-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        /* ========== TABS ========== */
        .budget-container .tab-menu-item.active {
          background: linear-gradient(135deg, #4A90A4 0%, #6BA893 100%);
          color: white;
        }
        
        /* ========== BUTTONS ========== */
        .budget-container .btn-primary {
          background: linear-gradient(135deg, #4A90A4 0%, #6BA893 100%);
          border: none;
          box-shadow: 0 2px 8px rgba(74, 144, 164, 0.3);
        }
        
        /* ========== TOAST SUCCESS ========== */
        .budget-container .toast.success {
          background: linear-gradient(135deg, #4A90A4 0%, #6BA893 100%);
        }
      `}</style>
    </div>
  );
}

export default Budget;